/**
 * api-client.ts — Re-export wrapper for backwards compatibility.
 *
 * All core logic has been extracted to @rudi/api.
 * This file maintains the existing import interface so consumers don't need changes.
 */
import { createApiClient } from '@rudi/api/client';
import { normalizeUrl } from '@rudi/api/interceptors';
import { webPlatformAdapter } from './web-platform-adapter';
import { installGetHandoff, responseFromFetch } from './getHandoff';
import { takeBootAgenda, takeBootOverview } from './bootPrefetch';
import type { ActionChallenge, ActionChallengeStatus } from '@rudi/shared/types/chat';

// Re-export types and utilities from @rudi/api
export { getErrorMessage, type ApiResponse, type ApiError } from '@rudi/api/errors';

// Create the singleton API client using the web platform adapter
export const apiClient = createApiClient(webPlatformAdapter);

// First-screen data an opening app requests before its code is in.
export const getHandoff = installGetHandoff(apiClient, {
  onUnused: key => globalThis.performance?.mark?.(`rudi:open-prefetch:unused:${key}`),
});

// The Home agenda the entry script requested on a reload (see bootPrefetch.ts).
const bootAgenda = takeBootAgenda();
if (bootAgenda) getHandoff.adopt(bootAgenda.request, responseFromFetch(bootAgenda.response));
for (const started of takeBootOverview()) {
  getHandoff.adopt(started.request, responseFromFetch(started.response));
}

// URL normalizer — re-export with the old name for backwards compatibility
export const normalizeApiClientUrl = normalizeUrl;

export type ActionChallengeDecision = 'approve' | 'reject';

export async function getActionChallenge(challengeId: string): Promise<ActionChallenge> {
  const response = await apiClient.get<ActionChallenge>(
    `/action-challenges/${encodeURIComponent(challengeId)}`
  );
  return response.data;
}

export async function decideActionChallenge(
  challengeId: string,
  decision: ActionChallengeDecision
): Promise<ActionChallengeStatus> {
  const response = await apiClient.post<{ status: ActionChallengeStatus }>(
    `/action-challenges/${encodeURIComponent(challengeId)}/${decision}`
  );
  return response.data.status;
}

// --- Legacy in-memory token store ---
// Some consumers (LocalAuthContext, OidcTokenSync, terminal, sockets) call
// setAccessToken / getAccessToken directly on the module. Keep this working
// as a sync in-memory cache that sits alongside the adapter.
let accessToken: string | null = null;

// Callback for refreshing Keycloak tokens (set by OidcTokenSync component)
let keycloakRefreshCallback: (() => Promise<string | null>) | null = null;

/**
 * Set access token (called from AuthContext or OidcTokenSync)
 */
export function setAccessToken(token: string | null) {
  accessToken = token;
}

/**
 * Get current access token
 */
export function getAccessToken(): string | null {
  return accessToken;
}

/**
 * Register Keycloak refresh callback
 * Called by OidcTokenSync component to provide token refresh capability
 */
export function setKeycloakRefreshCallback(callback: (() => Promise<string | null>) | null) {
  keycloakRefreshCallback = callback;
}

/**
 * Refresh access token
 * Tries Keycloak refresh first, falls back to legacy refresh
 */
export async function refreshToken(): Promise<string | null> {
  try {
    if (keycloakRefreshCallback) {
      const newToken = await keycloakRefreshCallback();
      if (newToken) {
        setAccessToken(newToken);
        return newToken;
      }
    }

    // The interceptor in @rudi/api handles refresh via the adapter.
    // For direct callers, trigger refresh via the adapter path.
    const refreshTokenValue = await webPlatformAdapter.getRefreshToken();
    if (!refreshTokenValue) {
      return null;
    }

    const apiBaseUrl = webPlatformAdapter.getApiBaseUrl();
    const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: refreshTokenValue }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    setAccessToken(data.accessToken);
    await webPlatformAdapter.setAccessToken(data.accessToken);
    if (data.refreshToken) {
      await webPlatformAdapter.setRefreshToken(data.refreshToken);
    }
    return data.accessToken;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    // Session is unrecoverable — trigger the standard expiry flow (clear state + redirect).
    await webPlatformAdapter.onSessionExpired();
    return null;
  }
}

/**
 * Update Claude session mode
 */
export async function updateSessionMode(sessionId: string, mode: string) {
  const response = await apiClient.patch(`/chat/sessions/${sessionId}/mode`, { mode });
  return response.data;
}
